import { BarChart3, GitBranch, PieChart } from 'lucide-react';
import D3BarChart from '@/components/analytics/D3BarChart';
import D3PieChart, { type PieDatum } from '@/components/analytics/D3PieChart';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { IDashboardSummary } from '@/types/dashboard';

const RUN_STATUS_COLORS = [
  '#22c55e',
  '#3b82f6',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#06b6d4',
  '#64748b',
  '#ec4899',
];

function formatStatusLabel(key: string): string {
  return key.replace(/_/g, ' ');
}

function ExerciseLifecycleBar({ exercises }: { exercises: IDashboardSummary['exercises'] }) {
  const total = exercises.total;
  if (total <= 0) {
    return <p className="text-sm text-muted-foreground py-6 text-center">No exercises yet</p>;
  }

  const segments = [
    {
      key: 'ready',
      label: 'Ready',
      count: exercises.approved_and_prepared,
      className: 'bg-emerald-500 dark:bg-emerald-600',
    },
    {
      key: 'appr',
      label: 'Approved, not prepared',
      count: exercises.approved_not_prepared,
      className: 'bg-sky-500 dark:bg-sky-600',
    },
    {
      key: 'draft',
      label: 'Not approved',
      count: exercises.not_approved,
      className: 'bg-slate-400 dark:bg-slate-500',
    },
  ];

  return (
    <div className="space-y-3">
      <div className="flex h-10 w-full rounded-lg overflow-hidden border border-border shadow-inner">
        {segments.map((s) => {
          const pct = (s.count / total) * 100;
          if (pct <= 0) return null;
          return (
            <div
              key={s.key}
              className={`${s.className} min-w-0 transition-all`}
              style={{ width: `${pct}%` }}
              title={`${s.label}: ${s.count} (${pct.toFixed(1)}%)`}
            />
          );
        })}
      </div>
      <ul className="flex flex-wrap gap-x-4 gap-y-2 text-xs">
        {segments.map((s) => (
          <li key={s.key} className="flex items-center gap-2 text-muted-foreground">
            <span className={`inline-block w-2.5 h-2.5 rounded-sm shrink-0 ${s.className}`} />
            <span>
              {s.label}: <span className="font-medium text-foreground tabular-nums">{s.count}</span>
            </span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-muted-foreground">
        Total exercises: <span className="font-medium text-foreground">{total}</span> · TTX enabled:{' '}
        <span className="font-medium text-foreground">{exercises.ttx_enabled}</span>
      </p>
    </div>
  );
}

function ParticipationFunnel({ part }: { part: IDashboardSummary['participation'] }) {
  const max = Math.max(part.inject_deliveries, 1);
  const rows = [
    { label: 'Inject deliveries', value: part.inject_deliveries, tone: 'bg-primary' },
    { label: 'With participant response', value: part.inject_deliveries_with_response, tone: 'bg-sky-500' },
    { label: 'Awaiting evaluation', value: part.inject_deliveries_pending_evaluation, tone: 'bg-amber-500' },
  ];

  return (
    <div className="space-y-4">
      {rows.map((row) => (
        <div key={row.label}>
          <div className="flex justify-between text-xs mb-1.5 gap-2">
            <span className="text-muted-foreground">{row.label}</span>
            <span className="font-semibold tabular-nums text-foreground">{row.value}</span>
          </div>
          <div className="h-2.5 rounded-full bg-muted overflow-hidden">
            <div
              className={`h-full rounded-full ${row.tone} transition-all`}
              style={{ width: `${Math.min(100, (row.value / max) * 100)}%` }}
            />
          </div>
        </div>
      ))}
      <p className="text-xs text-muted-foreground pt-1">
        Run participant rows:{' '}
        <span className="font-medium text-foreground tabular-nums">{part.run_participants}</span>
      </p>
    </div>
  );
}

export function DashboardVisuals({ data }: { data: IDashboardSummary }) {
  const cl = data.content_library;
  const contentLabels = ['Injects', 'Phases', 'Flags', 'Categories', 'Teams'];
  const contentValues = [cl.injects, cl.phases, cl.flags, cl.categories, data.teams.total];

  const statusEntries = Object.entries(data.runs.by_status)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);

  const runPieData: PieDatum[] = statusEntries.map(([label, value], i) => ({
    label: formatStatusLabel(label),
    value,
    color: RUN_STATUS_COLORS[i % RUN_STATUS_COLORS.length],
  }));

  const notifTotal = data.notifications.total || 1;
  const notifRead = Math.max(0, data.notifications.total - data.notifications.unread);
  const notifPieData: PieDatum[] = [
    { label: 'Read', value: notifRead, color: '#94a3b8' },
    { label: 'Unread', value: data.notifications.unread, color: '#3b82f6' },
  ];

  const modeBarLabels = data.exercises.by_mode.map((m) => m.name || m.code || '—').slice(0, 8);
  const modeBarValues = data.exercises.by_mode.map((m) => m.count).slice(0, 8);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <BarChart3 className="h-4 w-4" />
        <span>Visual overview</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card className="overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <GitBranch className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              Exercise lifecycle
            </CardTitle>
            <CardDescription>Share of exercises by approval / preparation state</CardDescription>
          </CardHeader>
          <CardContent>
            <ExerciseLifecycleBar exercises={data.exercises} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <PieChart className="h-5 w-5 text-primary" />
              Runs by status
            </CardTitle>
            <CardDescription>Distribution of exercise run lifecycle states</CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center py-2">
            {runPieData.length === 0 ? (
              <p className="text-sm text-muted-foreground py-12">No runs recorded yet</p>
            ) : (
              <D3PieChart data={runPieData} width={280} height={260} innerRadius={52} legend />
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <Card className="xl:col-span-2 overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Content library volume</CardTitle>
            <CardDescription>Counts of injects, phases, flags, categories, and teams</CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="w-full overflow-x-auto rounded-lg border border-border/60 bg-muted/20 p-2">
              <div className="min-h-[300px] min-w-[520px]">
                {contentValues.some((n) => n > 0) ? (
                  <D3BarChart
                    labels={contentLabels}
                    data={contentValues}
                    width={640}
                    height={320}
                    barColor="#6366f1"
                  />
                ) : (
                  <p className="text-sm text-muted-foreground py-16 text-center">No content counts yet</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Exercises by mode</CardTitle>
            <CardDescription>Flag vs milestone (and other modes)</CardDescription>
          </CardHeader>
          <CardContent>
            {modeBarLabels.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">No mode data</p>
            ) : (
              <div className="w-full overflow-x-auto rounded-lg border border-border/60 bg-muted/20 p-2">
                <div className="min-h-[300px] min-w-[400px]">
                  <D3BarChart
                    labels={modeBarLabels}
                    data={modeBarValues}
                    width={520}
                    height={300}
                    barColor="#0d9488"
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">TTX participation</CardTitle>
            <CardDescription>Inject delivery pipeline (relative scale)</CardDescription>
          </CardHeader>
          <CardContent>
            <ParticipationFunnel part={data.participation} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Notifications</CardTitle>
            <CardDescription>Read vs unread in the relational store</CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center py-2">
            {data.notifications.total === 0 ? (
              <p className="text-sm text-muted-foreground py-12">No notifications</p>
            ) : (
              <D3PieChart data={notifPieData} width={260} height={240} innerRadius={44} legend />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
